import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Building2,
  Users,
  Wrench,
  FileText,
  Bell,
  ClipboardList,
  CheckCircle2,
  ArrowRight,
  Shield,
  BarChart3,
  MessageSquare,
  Briefcase,
  Home,
  UserPlus,
  FileCheck,
  Send,
  FolderOpen,
  BadgeCheck,
  Layers,
  Globe,
} from 'lucide-react';

export default function AboutUs() {
  const navigate = useNavigate();

  const steps = [
    {
      number: "01",
      title: "Register Your Complex",
      description: "Trustees or managing agents register their residential complex on the BodyCorp365 platform. Once registered, the system creates a dedicated digital environment for that building.",
      icon: Building2,
      color: "bg-blue-500",
    },
    {
      number: "02",
      title: "Invite Residents and Owners",
      description: "Residents and property owners receive invitations to join their complex on the platform. Once registered, they can access building notices, submit maintenance requests, and view important documents.",
      icon: UserPlus,
      color: "bg-emerald-500",
    },
    {
      number: "03",
      title: "Log Maintenance Requests",
      description: "Residents can report issues directly through the app. Each request becomes a tracked ticket that trustees or managing agents can assign to contractors and monitor until completion.",
      icon: Wrench,
      color: "bg-amber-500",
    },
    {
      number: "04",
      title: "Manage Contractors and Quotes",
      description: "Trustees or managing agents can request quotes from multiple contractors, compare pricing, and approve work through the platform.",
      icon: ClipboardList,
      color: "bg-purple-500",
    },
    {
      number: "05",
      title: "Keep Records Securely Stored",
      description: "Important documents such as insurance policies, meeting minutes, financial reports, and compliance certificates are stored in a central digital archive.",
      icon: FolderOpen,
      color: "bg-rose-500",
    },
    {
      number: "06",
      title: "Communicate with Residents",
      description: "Trustees and managing agents can send official notices, announcements, and updates directly to residents through the app.",
      icon: Send,
      color: "bg-cyan-500",
    },
  ];

  const trusteeBenefits = [
    {
      title: "Centralised Communication",
      description: "Send notices and updates to residents through a single platform instead of relying on fragmented communication channels.",
      icon: MessageSquare,
    },
    {
      title: "Maintenance Tracking",
      description: "Track maintenance requests from submission to completion, ensuring issues are addressed and recorded.",
      icon: Wrench,
    },
    {
      title: "Contractor Quote Comparison",
      description: "Request and compare multiple contractor quotes before approving work.",
      icon: ClipboardList,
    },
    {
      title: "Transparent Record Keeping",
      description: "Maintain an organised archive of documents and operational decisions.",
      icon: FileText,
    },
    {
      title: "Improved Accountability",
      description: "Create a clear record of maintenance requests, contractor work, and administrative actions.",
      icon: BadgeCheck,
    },
  ];

  const agentFeatures = [
    {
      title: "Multi-Complex Dashboard",
      description: "Switch easily between multiple buildings from a single account.",
      icon: Layers,
    },
    {
      title: "Maintenance Management",
      description: "Track maintenance tickets, assign contractors, and monitor progress.",
      icon: Wrench,
    },
    {
      title: "Resident Communication",
      description: "Send notices and updates to residents directly through the platform.",
      icon: Bell,
    },
    {
      title: "Document Management",
      description: "Store and share important documents with trustees and residents.",
      icon: FileText,
    },
    {
      title: "Contractor Marketplace",
      description: "Access a network of contractors and compare quotes before work is approved.",
      icon: Briefcase,
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div 
              className="flex items-center gap-2 cursor-pointer" 
              onClick={() => navigate('/')}
            >
              <img 
                src="/logo.png" 
                alt="BodyCorp365" 
                className="h-10 w-auto"
              />
              <span className="text-xl font-bold font-manrope text-slate-900">BodyCorp365</span>
            </div>
            <div className="hidden md:flex items-center gap-6">
              <a href="/#features" className="text-slate-600 hover:text-slate-900 transition-colors">Features</a>
              <a href="/#pricing" className="text-slate-600 hover:text-slate-900 transition-colors">Pricing</a>
              <button 
                onClick={() => navigate('/about')}
                className="text-blue-600 font-medium"
              >
                How It Works
              </button>
              <Button variant="outline" onClick={() => navigate('/login')}>Sign In</Button>
              <Button onClick={() => navigate('/signup')} className="bg-blue-600 hover:bg-blue-700">
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-manrope mb-6">
            How BodyCorp365 Works
          </h1>
          <p className="text-xl sm:text-2xl text-slate-300 mb-4">
            A Smarter Way to Manage Residential Complexes
          </p>
          <p className="text-lg text-slate-400 max-w-3xl mx-auto">
            BodyCorp365 is a digital platform designed to simplify communication, maintenance management, 
            and administration within residential complexes. Instead of relying on scattered emails, 
            WhatsApp groups, or paper records, BodyCorp365 centralises everything in one secure system.
          </p>
        </div>
      </section>

      {/* How It Works Steps */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold font-manrope text-slate-900 mb-4">
              Getting Started is Simple
            </h2>
            <p className="text-lg text-slate-600">Six steps to transform how your complex operates</p>
          </div>

          <div className="grid gap-8">
            {steps.map((step, index) => (
              <div 
                key={step.number}
                className={`flex flex-col md:flex-row items-start gap-6 p-6 rounded-2xl transition-all hover:shadow-lg ${
                  index % 2 === 0 ? 'bg-slate-50' : 'bg-white border border-slate-100'
                }`}
              >
                <div className={`flex-shrink-0 w-16 h-16 ${step.color} rounded-2xl flex items-center justify-center`}>
                  <step.icon className="w-8 h-8 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-sm font-bold text-slate-400">STEP {step.number}</span>
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{step.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* For Trustees Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-blue-50 to-emerald-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-100 text-blue-700 text-sm font-medium mb-6">
                <Shield className="w-4 h-4" />
                For Trustees
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold font-manrope text-slate-900 mb-4">
                Simplifying the Role of Trustees
              </h2>
              <p className="text-lg text-slate-600 mb-6">
                Serving as a trustee of a residential complex can be a demanding responsibility. 
                Trustees must manage maintenance issues, communicate with residents, work with 
                managing agents, and ensure proper governance of the property.
              </p>
              <p className="text-lg text-slate-600">
                BodyCorp365 provides trustees with the tools they need to manage these 
                responsibilities more efficiently.
              </p>
            </div>
            <div className="space-y-4">
              {trusteeBenefits.map((benefit) => (
                <Card key={benefit.title} className="border-0 shadow-md hover:shadow-lg transition-shadow">
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <benefit.icon className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900 mb-1">{benefit.title}</h3>
                        <p className="text-sm text-slate-600">{benefit.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* For Managing Agents Section */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1 space-y-4">
              {agentFeatures.map((feature) => (
                <Card key={feature.title} className="border-0 shadow-md hover:shadow-lg transition-shadow">
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                        <feature.icon className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900 mb-1">{feature.title}</h3>
                        <p className="text-sm text-slate-600">{feature.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-100 text-emerald-700 text-sm font-medium mb-6">
                <Briefcase className="w-4 h-4" />
                For Managing Agents
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold font-manrope text-slate-900 mb-4">
                A Digital Platform Built for Managing Agents
              </h2>
              <p className="text-lg text-slate-600 mb-6">
                Managing agents often oversee multiple residential complexes, each with its own 
                communication needs, maintenance issues, and administrative requirements.
              </p>
              <p className="text-lg text-slate-600">
                BodyCorp365 provides managing agents with a unified platform to manage all 
                their properties more efficiently.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-20 px-4 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <Globe className="w-16 h-16 text-blue-400 mx-auto mb-6" />
          <h2 className="text-3xl sm:text-4xl font-bold font-manrope mb-6">
            Why Residential Communities Choose BodyCorp365
          </h2>
          <p className="text-lg text-slate-300 mb-8">
            Residential complexes require efficient systems to operate smoothly. BodyCorp365 helps 
            communities move away from fragmented communication and manual processes by providing 
            a centralised digital platform.
          </p>
          <p className="text-xl text-slate-200 font-medium mb-10">
            The result is improved communication, faster maintenance resolution, and better 
            record keeping for residential communities.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-blue-600 hover:bg-blue-700 text-white px-8"
              onClick={() => navigate('/signup')}
            >
              Get Started Today
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-white/30 text-white hover:bg-white/10"
              onClick={() => navigate('/agent-signup')}
            >
              Managing Agent Signup
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-100 py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <img 
                src="/bodycorp365-logo.svg" 
                alt="BodyCorp365" 
                className="h-8 w-auto"
              />
              <span className="text-lg font-bold text-slate-900">BodyCorp365</span>
            </div>
            <div className="flex gap-6 text-slate-600">
              <a href="/" className="hover:text-slate-900 transition-colors">Home</a>
              <a href="/#features" className="hover:text-slate-900 transition-colors">Features</a>
              <a href="/#pricing" className="hover:text-slate-900 transition-colors">Pricing</a>
              <a href="/about" className="hover:text-slate-900 transition-colors">How It Works</a>
              <a href="/login" className="hover:text-slate-900 transition-colors">Sign In</a>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-slate-200 text-center text-slate-500 text-sm">
            © {new Date().getFullYear()} BodyCorp365. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
